import 'twin.macro';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FolderNode, FolderType } from '@teetsh/app/src/types/sequences';
import _orderBy from 'lodash-es/orderBy';
import { ArrowUpIcon as ArrowUp } from '@heroicons/react/20/solid';
import { ArrowDownIcon as ArrowDown } from '@heroicons/react/20/solid';
import { PreviewButton } from '@teetsh/app/src/containers/Sequences/List/Sidebar/PreviewButton';
import { useGetMenuBasedOnFolderType } from 'containers/Sequences/components/useGetMenuBasedOnFolderType';
import { EllipsisHorizontalIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Menu } from 'components/Menu';
import Button from 'components/Buttons/Button';

interface Props {
  folderId: string;
  type: FolderType;
  onRenameFolder?: () => void;
  onCreateFolder?: (v: string) => void;
  changeOrderFdp?: (
    index: number,
    direction: -1 | 1,
    items: string[],
    sequenceId: string,
  ) => void;
  onAddFdp?: (ficheDePrepId: string, sequenceId?: string) => void;
  index: number;
  folders: FolderNode[];
  parents: string[];
  previewFdp?: boolean;
  showAddFdpButton?: boolean;
  hiddenActions?: 'all';
}

const Arrows = ({
  index,
  onChangeOrder,
  items,
}: {
  index: number;
  onChangeOrder: (direction: -1 | 1) => any;
  items: object[];
}) => {
  const { t } = useTranslation(['sequence']);
  return (
    <>
      {index !== 0 && (
        <Menu.Item
          onClick={() => onChangeOrder(-1)}
          text={t('actions.moveUp')}
          icon={ArrowUp}
        />
      )}
      {index !== items.length - 1 && (
        <Menu.Item
          onClick={() => onChangeOrder(1)}
          text={t('actions.moveDown')}
          icon={ArrowDown}
        />
      )}
    </>
  );
};

export function SecondaryLabels(props: Props) {
  const {
    folderId,
    type,
    changeOrderFdp,
    onAddFdp,
    index,
    folders,
    parents,
    previewFdp,
    showAddFdpButton,
    hiddenActions,
  } = props;
  const { t } = useTranslation(['sequence']);
  const getMenuItems = useGetMenuBasedOnFolderType(hiddenActions);

  const folder = folders.find(f => f.id === folderId);
  if (!folder) {
    return null;
  }
  if (type === 'folder' || type === 'sequence') {
    const menuItems = getMenuItems(folder);
    if (menuItems.length === 0) {
      return null;
    }
    return (
      <div onClick={e => e.stopPropagation()}>
        <Menu portal>
          <Menu.Button
            aria-label={`Actions ${folder.title}`}
            minimal
            small
            icon={EllipsisHorizontalIcon}
          />
          <Menu.Items>
            {menuItems.map(action => (
              <Menu.Item
                key={action.title}
                intent={action.intent}
                onClick={() => action.onClick(folder)}
                text={action.title}
                icon={action.icon}
              />
            ))}
          </Menu.Items>
        </Menu>
      </div>
    );
  }

  if (type === 'fdp_standalone') {
    const menuItems = getMenuItems(folder);
    return (
      <div tw="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        {menuItems.length === 0 ? null : (
          <Menu portal>
            <Menu.Button
              aria-label={`Actions fdp ${folder.title}`}
              minimal
              small
              icon={EllipsisHorizontalIcon}
            />
            <Menu.Items>
              {menuItems.map(action => (
                <Menu.Item
                  key={action.title}
                  intent={action.intent}
                  onClick={() => action.onClick(folder)}
                  text={action.title}
                  icon={action.icon}
                />
              ))}
            </Menu.Items>
          </Menu>
        )}
        <>
          {previewFdp && (
            <div role="presentation" onClick={e => e.stopPropagation()}>
              <PreviewButton fdpId={folderId} />
            </div>
          )}
          {showAddFdpButton && onAddFdp && (
            <Button
              minimal
              tw="bg-neutral-quiet"
              small
              icon={PlusIcon}
              onClick={() => onAddFdp(folderId)}
            />
          )}
        </>
      </div>
    );
  }

  if (type === 'fdp') {
    const sequenceId = parents[parents.length - 1];
    const menuItems = getMenuItems({
      ...folder,
      parentId: sequenceId,
    });
    const orderedFichesDePrep = _orderBy(
      folders.filter(f => f.type === 'fdp'),
      ['order'],
    );
    const countFdpInSequence = orderedFichesDePrep.length;
    const idxFdpInSequence = orderedFichesDePrep.findIndex(
      f => f.id === folder.id,
    );
    const seancePositionInSequence =
      idxFdpInSequence !== -1 && countFdpInSequence > 0
        ? `${idxFdpInSequence + 1}/${countFdpInSequence}`
        : undefined;

    return (
      <div tw="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        {!changeOrderFdp && menuItems.length === 0 ? null : (
          <Menu portal>
            <Menu.Button
              aria-label={`Actions fdp ${folder.title}`}
              minimal
              small
              icon={EllipsisHorizontalIcon}
            />
            <Menu.Items>
              {changeOrderFdp && (
                <Arrows
                  index={index}
                  onChangeOrder={(direction: -1 | 1) =>
                    changeOrderFdp(
                      index,
                      direction,
                      _orderBy(folders, ['order'])
                        .filter(f => f.type === 'fdp')
                        .map(s => s.id),
                      sequenceId,
                    )
                  }
                  items={folders.filter(f => f.type === 'fdp')}
                />
              )}
              {menuItems.map(action => (
                <Menu.Item
                  key={action.title}
                  intent={action.intent}
                  onClick={() => action.onClick(folder)}
                  text={action.title}
                  icon={action.icon}
                />
              ))}
            </Menu.Items>
          </Menu>
        )}
        {previewFdp && (
          <div role="presentation" onClick={e => e.stopPropagation()}>
            <PreviewButton
              fdpId={folderId}
              detail={`${t('sequence:seance_one')} ${seancePositionInSequence}`}
            />
          </div>
        )}
        {showAddFdpButton && onAddFdp && (
          <Button
            minimal
            tw="bg-neutral-quiet"
            small
            icon={PlusIcon}
            onClick={() => onAddFdp(folderId)}
          />
        )}
      </div>
    );
  }

  return null;
}
